/**
 * src/shared/constants.ts
 * Responsabilidad: Definición de constantes del juego compartidas y configuración del servidor
 * (dimensiones del mapa, velocidad de movimiento, tasa de ticks, elementos y límites de jugadores).
 * Ver references/room.md y references/schema.md del skill para contexto.
 */

export const MAP_WIDTH = 3000;
export const MAP_HEIGHT = 3000;
export const ARENA_CORNER_RADIUS = 180;
export const MOVE_SPEED = 9.2;
export const TICK_RATE = 30; // 30 ticks por segundo (intervalo de ~33.33ms)
export const ELEMENTS = ["fire", "water", "plant", "electric", "earth"] as const;
export type ElementType = typeof ELEMENTS[number];
export const MAX_PLAYERS = 50;
export const SERVER_PORT = 2567;
export const MAX_MOVE_MESSAGES_PER_SECOND = 40;
export const MAX_MOVE_BURST = 10; // Capacidad de ráfaga del token bucket por cliente

// ==== Sprint 3: Comida y crecimiento ====
/** Masa inicial de cada jugador al entrar a la sala */
export const MASS_INITIAL = 100;
/** Masa que aporta cada comida al ser absorbida */
export const FOOD_MASS = 6;
/** Cantidad total de comidas en el mapa (pool fijo) */
export const FOOD_COUNT = 500;
/** Exponente del decaimiento de velocidad con la masa: v = MOVE_SPEED * (MASS_INITIAL / mass) ^ VELOCITY_MASS_EXPONENT */
export const VELOCITY_MASS_EXPONENT = 0.4;
/** Margen interior (px) desde los bordes del mapa donde puede aparecer comida */
export const FOOD_SPAWN_MARGIN = 10;

// ==== Sprint 4: Colisiones entre jugadores, muerte y respawn ====
/** Ratio minimo de masa para poder devorar a otro jugador (A come a B si A.mass >= B.mass * EAT_RATIO) */
export const EAT_RATIO = 1.25;
/** Tiempo de espera en ms para reaparecer tras morir */
export const RESPAWN_DELAY_MS = 3000;
/** Delay extendido de respawn cuando el jugador tiene opciones de rewarded disponibles (ms) */
export const RESPAWN_DELAY_EXTENDED_MS = 6000;
/** Margen interior (px) desde los bordes del mapa para spawns y respawns de jugadores */
export const PLAYER_SPAWN_MARGIN = 100;
/** Duracion en ms de la animacion de shrink de muerte en el cliente */
export const DEATH_ANIMATION_MS = 300;

// ==== Sprint 5: Reacciones elementales ====
/** Cooldown por pareja de jugadores tras aplicar un counter (ms) */
export const COUNTER_COOLDOWN_MS = 2000;
/** Ratio minimo de masa atacante/defensor para efectividad 100% */
export const EFFECTIVENESS_FULL_RATIO = 0.50;
/** Ratio minimo de masa atacante/defensor para efectividad 50% */
export const EFFECTIVENESS_HALF_RATIO = 0.25;
/** Efectividad a plena potencia */
export const EFFECTIVENESS_FULL = 1.00;
/** Efectividad media */
export const EFFECTIVENESS_HALF = 0.50;
/** Efectividad minima */
export const EFFECTIVENESS_QUARTER = 0.25;
/** Radio de la cadena electrica (px) */
export const CHAIN_RADIUS = 300;
/** Pct de dano que reciben los water alcanzados por la cadena electrica */
export const CHAIN_DAMAGE_PCT = 0.10;
/** Fraccion del dano infligido que se transfiere al atacante por defecto */
export const COUNTER_MASS_TO_ATTACKER_DEFAULT = 0.50;
/** Masa minima de un jugador vivo tras recibir dano elemental (no puede morir por counter) */
export const MASS_MIN_ALIVE = 30;
/** Duracion visual del efecto elemental en el cliente (ms) */
export const ELEMENTAL_HIT_DURATION_MS = 450;

/** Dano base de water -> fire */
export const WATER_VS_FIRE_DAMAGE = 0.20;
/** Dano base de fire -> plant */
export const FIRE_VS_PLANT_DAMAGE = 0.25;
/** Dano base de plant -> earth */
export const PLANT_VS_EARTH_DAMAGE = 0.15;
/** Dano base de earth -> electric */
export const EARTH_VS_ELECTRIC_DAMAGE = 0.30;
/** Dano base de electric -> water */
export const ELECTRIC_VS_WATER_DAMAGE = 0.20;

/** Regla de counter elemental entre dos elementos */
export interface ElementalCounterRule {
    attacker: ElementType;
    defender: ElementType;
    baseDamagePct: number;
    attackerGainRatio: number;
    isChainLightning?: boolean;
}

/** Umbrales de efectividad segun la diferencia de tamano (atacante/defensor) */
export const SIZE_EFFECTIVENESS_THRESHOLDS = [
    { minRatio: 0.50, multiplier: 1.00 },
    { minRatio: 0.25, multiplier: 0.50 },
    { minRatio: 0.00, multiplier: 0.25 },
] as const;

/** Tabla de counters elementales indexada por "attacker->defender" */
export const ELEMENTAL_COUNTERS: Record<string, ElementalCounterRule> = {
    "water->fire": {
        attacker: "water",
        defender: "fire",
        baseDamagePct: WATER_VS_FIRE_DAMAGE,
        attackerGainRatio: COUNTER_MASS_TO_ATTACKER_DEFAULT, // 50% del dano infligido se transfiere al atacante (no 50% de la masa del defensor)
    },
    "fire->plant": {
        attacker: "fire",
        defender: "plant",
        baseDamagePct: FIRE_VS_PLANT_DAMAGE,
        attackerGainRatio: COUNTER_MASS_TO_ATTACKER_DEFAULT,
    },
    "plant->earth": {
        attacker: "plant",
        defender: "earth",
        baseDamagePct: PLANT_VS_EARTH_DAMAGE,
        attackerGainRatio: COUNTER_MASS_TO_ATTACKER_DEFAULT,
    },
    "earth->electric": {
        attacker: "earth",
        defender: "electric",
        baseDamagePct: EARTH_VS_ELECTRIC_DAMAGE,
        attackerGainRatio: 0.0, // Earth es toma a tierra: no gana masa, disipa el 100%
    },
    "electric->water": {
        attacker: "electric",
        defender: "water",
        baseDamagePct: ELECTRIC_VS_WATER_DAMAGE,
        attackerGainRatio: COUNTER_MASS_TO_ATTACKER_DEFAULT,
        isChainLightning: true,
    },
};

// ==== Sprint 6: Bots y Jugadores IA ====
/** Cantidad mínima total de jugadores activos (humanos + bots) en la arena */
export const MIN_ARENA_PLAYERS = 10;
export const MIN_BOTS = 0;
export const MAX_BOTS = 10;
/** Radio de deteccion de otros jugadores para huida o caza (px) */
export const BOT_AGGRO_RADIUS = 400;
/** Ratio de masa requerido sobre el bot para que este huya */
export const BOT_FLEE_RATIO = 1.25;
/** Ratio de masa que debe tener el bot sobre el objetivo para iniciar caza */
export const BOT_HUNT_RATIO = 1.25;
/** Distancia proyectada hacia la que apunta el vector de huida (px) */
export const BOT_FLEE_DISTANCE = 500;

// ==== Sprint 11: Power-ups ====
/** Tipos de power-ups disponibles */
export type PowerUpType = "invulnerability" | "speed" | "shield" | "double-mass" | "freeze";
/** Lista completa de tipos de power-ups */
export const POWERUP_TYPES: PowerUpType[] = ["invulnerability", "speed", "shield", "double-mass", "freeze"];
/** Cantidad de power-ups simultaneos en el mapa */
export const POWERUP_POOL_SIZE = 5;
/** Radio de colision de los power-ups (px) */
export const POWERUP_RADIUS = 16;
/** Margen interior desde los bordes del mapa para spawnear power-ups */
export const POWERUP_SPAWN_MARGIN = 150;
/** Tiempo de respawn tras ser consumido (ms) */
export const POWERUP_RESPAWN_DELAY_MS = 12000;
/** Duraciones de cada power-up (ms) */
export const POWERUP_DURATION_INVULNERABILITY_MS = 5000;
export const POWERUP_DURATION_SPEED_MS = 8000;
export const POWERUP_DURATION_DOUBLE_MASS_MS = 10000;
/** Duracion del freeze aplicado (ms) */
export const FREEZE_DURATION_MS = 1500;
/** Cooldown del caster tras recoger un freeze (ms) */
export const FREEZE_CASTER_COOLDOWN_MS = 8000;
/** Radio del efecto freeze (px) */
export const FREEZE_RADIUS = 500;
/** Multiplicador de velocidad del power-up speed */
export const SPEED_BOOST_MULTIPLIER = 2.0;
/** Multiplicador de masa del power-up double-mass */
export const DOUBLE_MASS_MULTIPLIER = 2.0;

/** Configuracion visual y de balance de cada power-up */
export interface PowerUpConfig {
    type: PowerUpType;
    name: string;
    emoji: string;
    color: string;
    durationMs: number;
    weight: number; // Peso para spawn ponderado
}

/** Configuracion de los 5 power-ups */
export const POWERUP_CONFIG: Record<PowerUpType, PowerUpConfig> = {
    invulnerability: {
        type: "invulnerability",
        name: "INVULNERABLE",
        emoji: "⭐",
        color: "#fbbf24",
        durationMs: POWERUP_DURATION_INVULNERABILITY_MS,
        weight: 10,
    },
    speed: {
        type: "speed",
        name: "VELOCIDAD",
        emoji: "⚡",
        color: "#38bdf8",
        durationMs: POWERUP_DURATION_SPEED_MS,
        weight: 35,
    },
    shield: {
        type: "shield",
        name: "ESCUDO",
        emoji: "🛡️",
        color: "#3b82f6",
        durationMs: 0, // 1 uso, no duracion
        weight: 20,
    },
    "double-mass": {
        type: "double-mass",
        name: "DOBLE MASA",
        emoji: "✨",
        color: "#10b981",
        durationMs: POWERUP_DURATION_DOUBLE_MASS_MS,
        weight: 30,
    },
    freeze: {
        type: "freeze",
        name: "CONGELAR",
        emoji: "❄️",
        color: "#a5f3fc",
        durationMs: 0, // Instantaneo
        weight: 5,
    },
};

// ==== Sprint 11: Zoom dinamico ====
/** Masa a partir de la cual el zoom empieza a reducirse */
export const ZOOM_MASS_THRESHOLD = 100;
/** Zoom minimo (cuando el jugador es muy grande) */
export const ZOOM_MIN = 0.42;
/** Zoom maximo (cuando el jugador es chico) */
export const ZOOM_MAX = 1.35;
/** Factor de suavizado del zoom (0-1, mas alto = mas rapido) */
export const ZOOM_SMOOTHING = 0.08;
export const ZOOM_EXPONENT = 0.34;
/** Fracción máxima del lado menor de la pantalla que puede ocupar el diámetro del jugador (26%) */
export const MAX_PLAYER_SCREEN_RATIO = 0.26;
/** Zoom manual minimo (rueda del mouse) */
export const MANUAL_ZOOM_MIN = 0.65;
/** Zoom manual maximo (rueda del mouse) */
export const MANUAL_ZOOM_MAX = 1.65;
/** Incremento de zoom por click de rueda */
export const MANUAL_ZOOM_STEP = 0.08;

// ==== Sprint 11: Gameplay y Personalizacion ====
/** Distancia del empuje al romper el escudo (px) */
export const SHIELD_KNOCKBACK_DISTANCE = 150;

/** Nombres aleatorios para los bots */
export const BOT_NAMES = [
    "Ares", "Nova", "Kai", "Zeta", "Atlas", "Iris", "Orion", "Lyra",
    "Nyx", "Rhea", "Cosmo", "Vega", "Hades", "Zeus", "Atenea", "Hera",
    "Poseidon", "Apolo", "Artemisa", "Hermes", "Selene", "Helios", "Gaia",
    "Cronos", "Eos", "Hypnos", "Thanatos", "Nike", "Eris", "Morpheus",
    "Circe", "Medusa", "Perseo", "Andromeda", "Casiopea", "Aquiles",
    "Ulises", "Penelope", "Heracles", "Pandora", "Prometeo", "Orfeo"
];

/** Lista de skins disponibles para jugadores y bots */
export const SKIN_IDS = ["default", "ring", "dotted", "metallic", "eyes", "smile"] as const;
export type SkinId = typeof SKIN_IDS[number];

/** Cooldown entre cambios de elemento (ms) */
export const ELEMENT_CHANGE_COOLDOWN_MS = 30000;

// ==== Sprint 14: Eventos y spawn seguro ====
/** Eventos disponibles */
export type EventType = "food-rain" | "meteor-shower" | "poison-zone" | "gravity-well";

/** Duracion del aviso previo a cada evento (ms) */
export const EVENT_WARNING_MS = 30000;

/** Duracion maxima de un evento (ms) */
export const EVENT_DURATION_MS = 30000;

/** Intervalo entre eventos (ms). Los eventos se rotan. */
export const EVENT_INTERVAL_MS = 150000; // 2:30

/** Configuracion de cada evento */
export interface EventConfig {
    type: EventType;
    name: string;
    emoji: string;
    color: string;
    isPositive: boolean;
    weight: number; // Peso para rotacion
}

export const EVENT_CONFIG: Record<EventType, EventConfig> = {
    "food-rain": {
        type: "food-rain",
        name: "Lluvia de Comida",
        emoji: "🍬",
        color: "#fbbf24",
        isPositive: true,
        weight: 50,
    },
    "meteor-shower": {
        type: "meteor-shower",
        name: "Lluvia de Meteoritos",
        emoji: "☄️",
        color: "#ef4444",
        isPositive: false,
        weight: 20,
    },
    "poison-zone": {
        type: "poison-zone",
        name: "Zona de Veneno",
        emoji: "☠️",
        color: "#22c55e",
        isPositive: false,
        weight: 20,
    },
    "gravity-well": {
        type: "gravity-well",
        name: "Iman Gravitatorio",
        emoji: "🌀",
        color: "#a78bfa",
        isPositive: false,
        weight: 10,
    },
};

/** Radio de la zona del evento (px) */
export const EVENT_ZONE_RADIUS = 300;

/** Cantidad de comidas en la lluvia de comida */
export const FOOD_RAIN_COUNT = 50;

/** Distancia minima a otros jugadores para spawn seguro (px) */
export const SAFE_SPAWN_MIN_DISTANCE = 300;

/** Intentos maximos de busqueda de spawn seguro */
export const SAFE_SPAWN_MAX_ATTEMPTS = 30;

/** Configuracion del evento meteor-shower */
export const METEOR_COUNT = 10;
export const METEOR_DAMAGE_PCT = 0.25; // 25% de masa perdida
export const METEOR_INTERVAL_MS = 1000; // un meteorito por segundo durante 10s
export const METEOR_IMPACT_RADIUS = 60; // radio del area de impacto (px)
export const METEOR_WARNING_MS = 1500; // aviso antes del impacto (ms)

/** Configuracion del evento poison-zone */
export const POISON_RADIUS = 400;              // radio de la zona (px)
export const POISON_DAMAGE_PCT_PER_SEC = 0.05; // 5% de masa perdida por segundo
export const POISON_TICK_MS = 1000;            // intervalo entre ticks de daño

/** Configuracion del evento gravity-well */
export const GRAVITY_RADIUS = 250;                    // radio de atraccion (px)
export const GRAVITY_PULL_STRENGTH = 0.35;            // factor de atraccion (0-1)
export const GRAVITY_CORE_RADIUS = 40;                // radio del nucleo (px)
export const GRAVITY_DAMAGE_PCT = 0.20;               // 20% masa perdida al capturar

// ==== Sprint 14C: Propulsión Alquímica (Barra Espaciadora) ====
/** Masa mínima requerida para poder activar y mantener la propulsión (evita suicidio accidental) */
export const BOOST_MIN_MASS = 120;
/** Multiplicador de velocidad mientras se mantiene presionada la barra espaciadora */
export const BOOST_SPEED_MULTIPLIER = 1.65;
/** Tope máximo de multiplicador de velocidad al combinar Power-Up Speed + Barra Espaciadora */
export const MAX_COMBINED_SPEED_MULTIPLIER = 2.35;
/** Costo base fijo de masa por segundo al usar propulsión */
export const BOOST_BASE_DRAIN_PER_SEC = 8;
/** Costo proporcional de masa por segundo (6% de la masa actual por segundo) */
export const BOOST_PCT_DRAIN_PER_SEC = 0.06;

// ==== Sprint 14C: Progresión, Combos y Killstreaks ====
/** Ventana máxima de tiempo (ms) entre acciones ofensivas (kills o counters) para mantener el combo */
export const COMBO_WINDOW_MS = 4000;
/** Bono de masa por cada nivel de combo activo (multiplicado por comboCount, máx +50) */
export const COMBO_MASS_BONUS_STEP = 10;
export const COMBO_MASS_BONUS_MAX = 50;
/** Bono de masa por neutralizar (Shutdown) a un jugador en racha (>= 3 kills): +40 por cada kill de su racha */
export const SHUTDOWN_BOUNTY_PER_KILL = 40;

export const KILLSTREAK_ANNOUNCEMENTS: Record<number, { title: string; emoji: string; color: string }> = {
    3: { title: "DOMINANDO", emoji: "🔥", color: "#f97316" },
    5: { title: "IMPARABLE", emoji: "⚡", color: "#facc15" },
    8: { title: "SEDIENTO DE ALQUIMIA", emoji: "💀", color: "#ef4444" },
    12: { title: "DIOS ALQUIMISTA", emoji: "👑", color: "#a855f7" },
};

// ==== Sprint 19: Equilibrio de Escala de Titanes (Anti-Overflow de Arena) ====
/** Masa máxima alcanzable por un jugador para evitar cubrir todo el mapa de 3000x3000 */
export const MAX_PLAYER_MASS = 50000;
/** Masa a partir de la cual empieza el decaimiento metabólico suave (estilo Agar.io) */
export const MASS_DECAY_THRESHOLD = 5000;
/** Porcentaje del excedente de masa sobre MASS_DECAY_THRESHOLD que se drena por segundo (tasa ultra-suave del 0.05%/s) */
export const MASS_DECAY_RATE_PER_SEC = 0.0005;
/** Masa a partir de la cual entra en juego la saturación alquímica (rendimientos decrecientes) */
export const MASS_DIMINISHING_THRESHOLD = 8000;
/** Factor de reducción de ganancia de masa al superar MASS_DIMINISHING_THRESHOLD (50%) */
export const MASS_DIMINISHING_FACTOR = 0.5;

// ==== Sprint 20: Misiones Diarias, Logros y Economía Dual ====
export type QuestType =
    | "kill_players"
    | "eat_orbs"
    | "drain_mass_counter"
    | "reach_mass"
    | "survive_time"
    | "collect_powerups"
    | "login_streak"
    | "kill_all_elements"
    | "shutdown_bounty";

export interface QuestDefinition {
    id: string;
    type: QuestType;
    titleKey: string;
    title: string;
    description: string;
    target: number;
    rewardCrystals: number;
    rewardAstralOrbs: number;
    unit?: string;
    isAchievement?: boolean;
}

export const DAILY_QUESTS_POOL: QuestDefinition[] = [
    {
        id: "kill_players",
        type: "kill_players",
        titleKey: "quests.kill_players",
        title: "Elimina a 3 jugadores",
        description: "Devora a 3 rivales en la arena alquímica.",
        target: 3,
        rewardCrystals: 150,
        rewardAstralOrbs: 0,
        isAchievement: false,
    },
    {
        id: "eat_orbs",
        type: "eat_orbs",
        titleKey: "quests.eat_orbs",
        title: "Consume 400 nutrientes alquímicos",
        description: "Absorbe orbes de masa dispersos en el mapa.",
        target: 400,
        rewardCrystals: 120,
        rewardAstralOrbs: 0,
        isAchievement: false,
    },
    {
        id: "drain_mass_counter",
        type: "drain_mass_counter",
        titleKey: "quests.drain_mass_counter",
        title: "Drena 1,500 de masa con counters elementales",
        description: "Golpea a rivales aprovechando sus debilidades elementales.",
        target: 1500,
        rewardCrystals: 200,
        rewardAstralOrbs: 0,
        isAchievement: false,
    },
    {
        id: "reach_mass",
        type: "reach_mass",
        titleKey: "quests.reach_mass",
        title: "Alcanza 2,500 de masa en una partida",
        description: "Crece y mantente con vida hasta superar los 2,500 de masa.",
        target: 2500,
        rewardCrystals: 180,
        rewardAstralOrbs: 0,
        isAchievement: false,
    },
    {
        id: "survive_time",
        type: "survive_time",
        titleKey: "quests.survive_time",
        title: "Sobrevive 4 minutos en una sola vida",
        description: "Mantente con vida continuamente durante 240 segundos.",
        target: 240000,
        rewardCrystals: 150,
        rewardAstralOrbs: 0,
        unit: "ms",
        isAchievement: false,
    },
    {
        id: "collect_powerups",
        type: "collect_powerups",
        titleKey: "quests.collect_powerups",
        title: "Recoge 3 power-ups en la arena",
        description: "Encuentra y recoge potenciadores esparcidos en la arena.",
        target: 3,
        rewardCrystals: 120,
        rewardAstralOrbs: 0,
        isAchievement: false,
    },
];

export const PERMANENT_ACHIEVEMENTS: QuestDefinition[] = [
    {
        id: "login_streak_5",
        type: "login_streak",
        titleKey: "achievements.login_streak_5",
        title: "Inicia sesión 5 días seguidos",
        description: "Demuestra tu constancia ingresando a Alchem.io durante 5 días consecutivos.",
        target: 5,
        rewardCrystals: 500,
        rewardAstralOrbs: 15,
        isAchievement: true,
    },
    {
        id: "reach_titan_8k",
        type: "reach_mass",
        titleKey: "achievements.reach_titan_8k",
        title: "Alcanza 8,000 de masa",
        description: "Conviértete en un titán alcanzando la imponente masa de 8,000.",
        target: 8000,
        rewardCrystals: 800,
        rewardAstralOrbs: 20,
        isAchievement: true,
    },
    {
        id: "kill_with_all_elements",
        type: "kill_all_elements",
        titleKey: "achievements.kill_with_all_elements",
        title: "Logra 1 kill con cada uno de los 5 elementos",
        description: "Domina el fuego, agua, planta, eléctrico y tierra consiguiendo una baja con cada uno.",
        target: 5,
        rewardCrystals: 400,
        rewardAstralOrbs: 10,
        isAchievement: true,
    },
    {
        id: "shutdown_bounty",
        type: "shutdown_bounty",
        titleKey: "achievements.shutdown_bounty",
        title: "Neutraliza a 3 jugadores en racha de asesinatos",
        description: "Pon fin a la racha de al menos 3 rivales en racha asesina (Shutdown).",
        target: 3,
        rewardCrystals: 300,
        rewardAstralOrbs: 10,
        isAchievement: true,
    },
];

export const MAX_DAILY_AD_WATCHES = 3;
export const AD_WATCH_REWARD_ORBS = 2;
export const AD_WATCH_REWARD_CRYSTALS = 50;

/**
 * Selecciona determinísticamente 3 misiones diarias a partir de la fecha UTC (YYYY-MM-DD).
 */
export function getDailyQuestIdsForDate(dateStr: string): string[] {
    let h = 0;
    for (let i = 0; i < dateStr.length; i++) {
        h = Math.imul(31, h) + dateStr.charCodeAt(i) | 0;
    }
    const rng = () => {
        h = Math.imul(h ^ (h >>> 16), 2246822507) | 0;
        h = Math.imul(h ^ (h >>> 13), 3266489909) | 0;
        return ((h ^= h >>> 16) >>> 0) / 4294967296;
    };
    const pool = [...DAILY_QUESTS_POOL];
    const selected: QuestDefinition[] = [];
    for (let i = 0; i < 3 && pool.length > 0; i++) {
        const idx = Math.floor(rng() * pool.length);
        selected.push(pool.splice(idx, 1)[0]);
    }
    return selected.map((q) => q.id);
}

// =========================================================================
// Catálogo Oficial de Cosméticos y Economía Dual
// =========================================================================

export type CosmeticCategory = "eyes" | "mouth" | "glasses" | "tattoo" | "aura" | "thruster";
export type CosmeticCurrency = "crystals" | "orbs";

export interface CosmeticCatalogItem {
    id: string;
    label: string;
    price: number;
    currency: CosmeticCurrency;
    priceCrystals?: number; // Precio alternativo en cristales para Auras
    priceOrbs?: number;     // Precio alternativo en orbes
}

export const COSMETIC_CATALOG: Record<CosmeticCategory, Array<CosmeticCatalogItem>> = {
    eyes: [
        { id: "default", label: "Clásicos", price: 0, currency: "crystals" },
        { id: "anime", label: "Anime", price: 150, currency: "crystals" },
        { id: "sharingan", label: "Carmesí", price: 300, currency: "crystals" },
        { id: "starry", label: "Estelar", price: 450, currency: "crystals" },
        { id: "cyclops", label: "Cíclope", price: 600, currency: "crystals" },
        { id: "laser", label: "Cibernéticos", price: 750, currency: "crystals" },
        { id: "hypno", label: "Hipnótico", price: 900, currency: "crystals" },
        { id: "cat", label: "Felinos", price: 1100, currency: "crystals" },
        { id: "demon", label: "Demoniacos", price: 1350, currency: "crystals" },
        { id: "void", label: "Vacío", price: 1600, currency: "crystals" },
    ],
    mouth: [
        { id: "none", label: "Sin boca", price: 0, currency: "crystals" },
        { id: "smile", label: "Sonrisa", price: 200, currency: "crystals" },
        { id: "fangs", label: "Colmillos", price: 350, currency: "crystals" },
        { id: "uwu", label: "Kawaii", price: 500, currency: "crystals" },
        { id: "stitched", label: "Cosida", price: 700, currency: "crystals" },
        { id: "smirk", label: "Pícara", price: 850, currency: "crystals" },
        { id: "mustache", label: "Bigote", price: 1000, currency: "crystals" },
        { id: "grill", label: "Oro", price: 1200, currency: "crystals" },
        { id: "vampire", label: "Vampiro", price: 1400, currency: "crystals" },
        { id: "mask", label: "Ninja", price: 1650, currency: "crystals" },
    ],
    glasses: [
        { id: "none", label: "Ninguna", price: 0, currency: "crystals" },
        { id: "sunglasses", label: "Gafas Sol", price: 350, currency: "crystals" },
        { id: "cyber", label: "Visor Neón", price: 550, currency: "crystals" },
        { id: "monocle", label: "Monóculo", price: 800, currency: "crystals" },
        { id: "goggles", label: "Steampunk", price: 1000, currency: "crystals" },
        { id: "matrix", label: "Matrix", price: 1250, currency: "crystals" },
        { id: "heart", label: "Corazones", price: 1450, currency: "crystals" },
        { id: "nerd", label: "Profesor", price: 1650, currency: "crystals" },
        { id: "nightvision", label: "Visión Nocturna", price: 1900, currency: "crystals" },
        { id: "vr", label: "Visor VR", price: 2200, currency: "crystals" },
    ],
    tattoo: [
        { id: "none", label: "Ninguno", price: 0, currency: "crystals" },
        { id: "runes", label: "Runas", price: 500, currency: "crystals" },
        { id: "cracks", label: "Grietas", price: 750, currency: "crystals" },
        { id: "tribal", label: "Arcano", price: 1000, currency: "crystals" },
        { id: "dragon", label: "Dragón", price: 1300, currency: "crystals" },
        { id: "circuit", label: "Bio-Circuito", price: 1600, currency: "crystals" },
        { id: "flame_mark", label: "Marca Ígnea", price: 1900, currency: "crystals" },
        { id: "celestial", label: "Constelación", price: 2200, currency: "crystals" },
        { id: "curse", label: "Maldición", price: 2500, currency: "crystals" },
        { id: "alchemic_circle", label: "Círculo Alquímico", price: 3000, currency: "crystals" },
    ],
    aura: [
        { id: "none", label: "Ninguna", price: 0, currency: "orbs" },
        { id: "crown", label: "Corona Real", price: 50, currency: "orbs" },
        { id: "horns", label: "Cuernos Ígneos", price: 100, currency: "orbs" },
        { id: "spikes", label: "Cristales", price: 150, currency: "orbs" },
        { id: "phoenix", label: "Alas Fénix", price: 200, currency: "orbs" },
        { id: "halo", label: "Halo Sagrado", price: 250, currency: "orbs" },
        { id: "orbital_orbs", label: "Orbes Esferas", price: 300, currency: "orbs" },
        { id: "electric_storm", label: "Tormenta Rayos", price: 380, currency: "orbs" },
        { id: "abyssal_tendrils", label: "Zarcillos Sombríos", price: 450, currency: "orbs" },
        { id: "cosmic_nova", label: "Supernova Cósmica", price: 550, currency: "orbs" },
    ],
    thruster: [
        { id: "none", label: "Ninguno", price: 0, currency: "orbs" },
        { id: "alchemical", label: "Pulso Alquímico", price: 50, currency: "orbs" },
        { id: "sakura", label: "Pétalos Sakura", price: 100, currency: "orbs" },
        { id: "cosmic", label: "Polvo Cósmico", price: 150, currency: "orbs" },
        { id: "runic", label: "Glifos Rúnicos", price: 200, currency: "orbs" },
        { id: "bubbles", label: "Burbujas Prisma", price: 250, currency: "orbs" },
        { id: "musical", label: "Melodía Neón", price: 300, currency: "orbs" },
        { id: "gold_coins", label: "Monedas Midas", price: 350, currency: "orbs" },
        { id: "ghost_fire", label: "Fuego Fatuo", price: 400, currency: "orbs" },
        { id: "rainbow", label: "Arcoíris Prisma", price: 500, currency: "orbs" },
    ],
};

export function getCosmeticPrice(
    category: string,
    id: string,
    _chosenCurrency?: CosmeticCurrency
): { price: number; currency: CosmeticCurrency } {
    const cat = category as CosmeticCategory;
    const items = COSMETIC_CATALOG[cat];
    if (items) {
        const item = items.find((i) => i.id === id);
        if (item) {
            return { price: item.price, currency: item.currency };
        }
    }
    return { price: 0, currency: category === "aura" || category === "thruster" ? "orbs" : "crystals" };
}

// =========================================================================
// Sprint 21: Ruleta Alquímica Diaria (Giro de la Fortuna)
// =========================================================================

export interface RouletteSegment {
    index: number;
    type: "crystals" | "orbs" | "none" | "skin";
    amount: number;
    skinId?: string;
    label: string;
    weight: number; // Porcentaje de probabilidad (suma = 100)
    color: string;
}

export const ROULETTE_SEGMENTS: RouletteSegment[] = [
    { index: 0, type: "crystals", amount: 5, label: "+5 💎", weight: 24, color: "#1e293b" },
    { index: 1, type: "crystals", amount: 10, label: "+10 💎", weight: 18, color: "#0f172a" },
    { index: 2, type: "crystals", amount: 20, label: "+20 💎", weight: 14, color: "#1e293b" },
    { index: 3, type: "crystals", amount: 30, label: "+30 💎", weight: 9, color: "#0f172a" },
    { index: 4, type: "crystals", amount: 40, label: "+40 💎", weight: 6, color: "#1e293b" },
    { index: 5, type: "crystals", amount: 50, label: "+50 💎", weight: 4, color: "#0284c7" },
    { index: 6, type: "orbs", amount: 2, label: "+2 🔮", weight: 7, color: "#312e81" },
    { index: 7, type: "orbs", amount: 3, label: "+3 🔮", weight: 4, color: "#4338ca" },
    { index: 8, type: "orbs", amount: 4, label: "+4 🔮", weight: 2, color: "#4f46e5" },
    { index: 9, type: "orbs", amount: 5, label: "+5 🔮", weight: 1, color: "#6366f1" },
    { index: 10, type: "none", amount: 0, label: "Vacío 💨", weight: 10, color: "#334155" },
    { index: 11, type: "skin", amount: 0, skinId: "aura:phoenix", label: "Fénix 🌟", weight: 1, color: "#f59e0b" },
];

// =========================================================================
// Sprint 29: Pociones y Consumibles Tácticos (La Mesa del Alquimista)
// =========================================================================

export type PotionId = "speed" | "magnet" | "element_lock" | "repel" | "element_bomb" | "cosmic_food" | "rebirth";
export type PotionRarity = "common" | "rare" | "epic";

export interface PotionDefinition {
    id: PotionId;
    name: string;
    icon: string;
    priceCrystals: number;
    priceOrbs?: number;
    currency?: "crystals" | "orbs";
    durationMs: number;
    description: string;
    color: string;
    rarity: PotionRarity;
    radiusPx?: number;
    multiplier?: number;
    statValue?: number;
}

export const INITIAL_PLAYER_MASS = MASS_INITIAL;
export const POTION_MAX_BELT_SLOTS = 3;
export const POTION_DRINK_CAST_MS = 500;        // 0.5s bebiendo (vulnerable/inmóvil)
export const POTION_GLOBAL_COOLDOWN_MS = 10000; // 10s entre pociones

export const POTION_CATALOG: Record<PotionId, PotionDefinition> = {
    speed: {
        id: "speed",
        name: "Elixir de Velocidad",
        icon: "⚡",
        priceCrystals: 2,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 5000,
        description: "+30% de velocidad de movimiento durante 5 segundos.",
        color: "#00e5ff",
        rarity: "common",
        radiusPx: 0,
        multiplier: 100,
        statValue: 0,
    },
    magnet: {
        id: "magnet",
        name: "Imán de Esencia",
        icon: "🧲",
        priceCrystals: 3,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 6000,
        description: "Atrae automáticamente los orbes de comida cercanos (260px) durante 6 segundos.",
        color: "#ff4081",
        rarity: "common",
        radiusPx: 260,
        multiplier: 100,
        statValue: 0,
    },
    element_lock: {
        id: "element_lock",
        name: "Bloqueo Elemental",
        icon: "🔒",
        priceCrystals: 4,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 5000,
        description: "Impide que los rivales cercanos (250px) cambien de elemento durante 5 segundos.",
        color: "#ffd740",
        rarity: "rare",
        radiusPx: 250,
        multiplier: 100,
        statValue: 0,
    },
    repel: {
        id: "repel",
        name: "Onda de Purga",
        icon: "💥",
        priceCrystals: 5,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 1000,
        description: "Purga congelación/bloqueos propios y empuja lejos a los enemigos cercanos (240px).",
        color: "#b388ff",
        rarity: "rare",
        radiusPx: 240,
        multiplier: 100,
        statValue: 0,
    },
    element_bomb: {
        id: "element_bomb",
        name: "Bomba Elemental",
        icon: "💣",
        priceCrystals: 6,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 1000,
        description: "Transmuta a todos los rivales en 300px a un elemento aleatorio.",
        color: "#f43f5e",
        rarity: "epic",
        radiusPx: 300,
        multiplier: 100,
        statValue: 0,
    },
    cosmic_food: {
        id: "cosmic_food",
        name: "Comida Cósmica",
        icon: "🌟",
        priceCrystals: 5,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 30000,
        description: "Todo nutriente consumido otorga x3 de masa durante 30s.",
        color: "#a855f7",
        rarity: "rare",
        radiusPx: 0,
        multiplier: 300,
        statValue: 0,
    },
    rebirth: {
        id: "rebirth",
        name: "Renacimiento Alquímico",
        icon: "🕊️",
        priceCrystals: 6,
        priceOrbs: 0,
        currency: "crystals",
        durationMs: 60000,
        description: "Si eres devorado en combate, renaces conservando el 50% de tu masa.",
        color: "#10b981",
        rarity: "epic",
        radiusPx: 0,
        multiplier: 100,
        statValue: 50,
    },
};

// =========================================================================
// Sprint 30: Sistema de Emotes Sociales y Rueda Radial (44 Emotes Oficiales)
// =========================================================================

export type EmoteCurrency = "crystals" | "orbs";
export type EmoteTier = "common" | "rare" | "exclusive";

export interface EmoteDefinition {
    id: string;
    name: string;
    icon: string;
    svgUrl: string;
    price: number;
    currency: EmoteCurrency;
    tier: EmoteTier;
}

export const EMOTE_MAX_EQUIPPED = 5;
export const EMOTE_COOLDOWN_MS = 3000;   // 3s de cooldown anti-spam entre emotes
export const EMOTE_DURATION_MS = 2500;   // 2.5s flotando sobre el jugador

const TWEMOJI_BASE = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg";

export const EMOTE_CATALOG: Record<string, EmoteDefinition> = {
    // === Comunes (100 - 200 💎) ===
    wave: { id: "wave", name: "Hola / Paz", icon: "👋", svgUrl: `${TWEMOJI_BASE}/1f44b.svg`, price: 100, currency: "crystals", tier: "common" },
    scream: { id: "scream", name: "¡Susto!", icon: "😱", svgUrl: `${TWEMOJI_BASE}/1f631.svg`, price: 100, currency: "crystals", tier: "common" },
    skull: { id: "skull", name: "Peligro", icon: "💀", svgUrl: `${TWEMOJI_BASE}/1f480.svg`, price: 120, currency: "crystals", tier: "common" },
    cool: { id: "cool", name: "Gafas Pro", icon: "😎", svgUrl: `${TWEMOJI_BASE}/1f60e.svg`, price: 150, currency: "crystals", tier: "common" },
    rage: { id: "rage", name: "Furia", icon: "🔥", svgUrl: `${TWEMOJI_BASE}/1f525.svg`, price: 150, currency: "crystals", tier: "common" },
    joy: { id: "joy", name: "Risa", icon: "😂", svgUrl: `${TWEMOJI_BASE}/1f602.svg`, price: 200, currency: "crystals", tier: "common" },
    thumbs_up: { id: "thumbs_up", name: "Pulgar Arriba", icon: "👍", svgUrl: `${TWEMOJI_BASE}/1f44d.svg`, price: 100, currency: "crystals", tier: "common" },
    peace: { id: "peace", name: "Victoria / Paz", icon: "✌️", svgUrl: `${TWEMOJI_BASE}/270c.svg`, price: 100, currency: "crystals", tier: "common" },
    clap: { id: "clap", name: "Aplausos", icon: "👏", svgUrl: `${TWEMOJI_BASE}/1f44f.svg`, price: 120, currency: "crystals", tier: "common" },
    sweat: { id: "sweat", name: "Uff Sudor", icon: "😅", svgUrl: `${TWEMOJI_BASE}/1f605.svg`, price: 120, currency: "crystals", tier: "common" },
    think: { id: "think", name: "Pensativo", icon: "🤔", svgUrl: `${TWEMOJI_BASE}/1f914.svg`, price: 140, currency: "crystals", tier: "common" },
    winking: { id: "winking", name: "Guiño", icon: "😉", svgUrl: `${TWEMOJI_BASE}/1f609.svg`, price: 140, currency: "crystals", tier: "common" },
    sob: { id: "sob", name: "Llanto", icon: "😭", svgUrl: `${TWEMOJI_BASE}/1f62d.svg`, price: 160, currency: "crystals", tier: "common" },
    mind_blown: { id: "mind_blown", name: "Cerebro Roto", icon: "🤯", svgUrl: `${TWEMOJI_BASE}/1f92f.svg`, price: 180, currency: "crystals", tier: "common" },
    salute: { id: "salute", name: "Saludo Militar", icon: "🫡", svgUrl: `${TWEMOJI_BASE}/1fae1.svg`, price: 180, currency: "crystals", tier: "common" },
    eyes_peek: { id: "eyes_peek", name: "Mirada Atenta", icon: "👀", svgUrl: `${TWEMOJI_BASE}/1f440.svg`, price: 200, currency: "crystals", tier: "common" },

    // === Raros (250 - 500 💎) ===
    taunt: { id: "taunt", name: "¡Desafío!", icon: "😤", svgUrl: `${TWEMOJI_BASE}/1f624.svg`, price: 250, currency: "crystals", tier: "rare" },
    trophy: { id: "trophy", name: "Trofeo", icon: "🏆", svgUrl: `${TWEMOJI_BASE}/1f3c6.svg`, price: 300, currency: "crystals", tier: "rare" },
    dance: { id: "dance", name: "Baile", icon: "💃", svgUrl: `${TWEMOJI_BASE}/1f483.svg`, price: 400, currency: "crystals", tier: "rare" },
    crown: { id: "crown", name: "Corona", icon: "👑", svgUrl: `${TWEMOJI_BASE}/1f451.svg`, price: 500, currency: "crystals", tier: "rare" },
    swords: { id: "swords", name: "Duelo a Muerte", icon: "⚔️", svgUrl: `${TWEMOJI_BASE}/2694.svg`, price: 250, currency: "crystals", tier: "rare" },
    shield_iron: { id: "shield_iron", name: "Guardián de Hierro", icon: "🛡️", svgUrl: `${TWEMOJI_BASE}/1f6e1.svg`, price: 280, currency: "crystals", tier: "rare" },
    target: { id: "target", name: "En la Mira", icon: "🎯", svgUrl: `${TWEMOJI_BASE}/1f3af.svg`, price: 300, currency: "crystals", tier: "rare" },
    lightning: { id: "lightning", name: "Rayo Implacable", icon: "⚡", svgUrl: `${TWEMOJI_BASE}/26a1.svg`, price: 320, currency: "crystals", tier: "rare" },
    explosion: { id: "explosion", name: "Boom Caos", icon: "💥", svgUrl: `${TWEMOJI_BASE}/1f4a5.svg`, price: 350, currency: "crystals", tier: "rare" },
    ghost: { id: "ghost", name: "Espectro Cazador", icon: "👻", svgUrl: `${TWEMOJI_BASE}/1f47b.svg`, price: 380, currency: "crystals", tier: "rare" },
    fire_heart: { id: "fire_heart", name: "Pasión Alquímica", icon: "❤️🔥", svgUrl: `${TWEMOJI_BASE}/1f496.svg`, price: 400, currency: "crystals", tier: "rare" },
    hourglass: { id: "hourglass", name: "Tiempo Agotado", icon: "⏳", svgUrl: `${TWEMOJI_BASE}/23f3.svg`, price: 420, currency: "crystals", tier: "rare" },
    boxing_glove: { id: "boxing_glove", name: "Golpe de K.O.", icon: "🥊", svgUrl: `${TWEMOJI_BASE}/1f94a.svg`, price: 450, currency: "crystals", tier: "rare" },
    clown: { id: "clown", name: "Pobre Payaso", icon: "🤡", svgUrl: `${TWEMOJI_BASE}/1f921.svg`, price: 500, currency: "crystals", tier: "rare" },

    // === Épicos / Exclusivos (1 - 8 🔮) ===
    gem: { id: "gem", name: "Cristal Brillante", icon: "💎", svgUrl: `${TWEMOJI_BASE}/1f48e.svg`, price: 1, currency: "orbs", tier: "exclusive" },
    star: { id: "star", name: "Estrella Fugaz", icon: "🌟", svgUrl: `${TWEMOJI_BASE}/1f31f.svg`, price: 2, currency: "orbs", tier: "exclusive" },
    dragon: { id: "dragon", name: "Dragón", icon: "🐉", svgUrl: `${TWEMOJI_BASE}/1f409.svg`, price: 3, currency: "orbs", tier: "exclusive" },
    golden_skull: { id: "golden_skull", name: "Calavera Dorada", icon: "☠️", svgUrl: `${TWEMOJI_BASE}/2620.svg`, price: 5, currency: "orbs", tier: "exclusive" },
    black_hole: { id: "black_hole", name: "Agujero Negro", icon: "🕳️", svgUrl: `${TWEMOJI_BASE}/1f573.svg`, price: 2, currency: "orbs", tier: "exclusive" },
    magic_wand: { id: "magic_wand", name: "Vara Mágica", icon: "🪄", svgUrl: `${TWEMOJI_BASE}/1fa84.svg`, price: 2, currency: "orbs", tier: "exclusive" },
    crystal_ball: { id: "crystal_ball", name: "Oráculo de Cristal", icon: "🔮", svgUrl: `${TWEMOJI_BASE}/1f52e.svg`, price: 3, currency: "orbs", tier: "exclusive" },
    phoenix_bird: { id: "phoenix_bird", name: "Fénix Dorado", icon: "🦅", svgUrl: `${TWEMOJI_BASE}/1f985.svg`, price: 3, currency: "orbs", tier: "exclusive" },
    alien_boss: { id: "alien_boss", name: "Invasor del Vacío", icon: "👽", svgUrl: `${TWEMOJI_BASE}/1f47d.svg`, price: 4, currency: "orbs", tier: "exclusive" },
    ouroboros: { id: "ouroboros", name: "Serpiente Infinita", icon: "🐍", svgUrl: `${TWEMOJI_BASE}/1f40d.svg`, price: 4, currency: "orbs", tier: "exclusive" },
    comet: { id: "comet", name: "Cometa Destructor", icon: "☄️", svgUrl: `${TWEMOJI_BASE}/2604.svg`, price: 5, currency: "orbs", tier: "exclusive" },
    god_trident: { id: "god_trident", name: "Tridente Divino", icon: "🔱", svgUrl: `${TWEMOJI_BASE}/1f531.svg`, price: 6, currency: "orbs", tier: "exclusive" },
    diamond: { id: "diamond", name: "Diamante Trascendente", icon: "💎", svgUrl: `${TWEMOJI_BASE}/1f537.svg`, price: 7, currency: "orbs", tier: "exclusive" },
    king_crown: { id: "king_crown", name: "Monarca Supremo", icon: "👑", svgUrl: `${TWEMOJI_BASE}/1f451.svg`, price: 8, currency: "orbs", tier: "exclusive" },
};
export interface EmoteCatalogItem {
    id: string;
    name: string;
    priceCrystals: number;
    priceOrbs: number;
    rarity: string;
    svgUrl: string;
    icon?: string;
}

export const EMOTES_CATALOG: EmoteCatalogItem[] = Object.values(EMOTE_CATALOG).map((item) => ({
    id: item.id,
    name: item.name,
    priceCrystals: item.currency === "crystals" ? item.price : 0,
    priceOrbs: item.currency === "orbs" ? item.price : 0,
    rarity: item.tier,
    svgUrl: item.svgUrl,
    icon: item.icon,
}));

// =========================================================================
// Sprint 31: Bono de Bienvenida y Ofertas Diarias de Video en Tienda
// =========================================================================
export const WELCOME_BONUS_CRYSTALS = 200;
export const WELCOME_BONUS_ORBS = 10;
export type DailyShopAdType = "potion" | "crystals" | "orbs";
export const DAILY_SHOP_AD_CRYSTALS_REWARD = 20;
export const DAILY_SHOP_AD_ORBS_REWARD = 5;
